import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { joinFirmByInviteCode } from '../lib/db';
import { supabase } from '../services/supabaseClient';
import {
  Loader, AlertCircle, UserPlus, CheckCircle, KeyRound,
  Scale, Clock, ShieldCheck, Users,
  Gavel, CalendarCheck, ChevronRight,
  Mail, Lock, Bell, Volume2, Smartphone,
  Eye, EyeOff, Check, X as XIcon,
  Briefcase, BookUser, CalendarDays, Receipt,
  AlarmClock,
  Wallet, FileText, BarChart2, Activity, MessageSquare,
  DollarSign, ListChecks,
} from 'lucide-react';

// ─── Design system tokens ──────────────────────────────────────────────────────
// Primary brand blues
const BLUE_DEEP   = '#0F2A5F';   // left panel bg base
const BLUE_MID    = '#1E3A8A';   // left panel gradient end
const BLUE_BRAND  = '#1B2F6B';   // ticker bar / nav
const BLUE_UI     = '#3B82F6';   // icon accents
const BLUE_SOFT   = '#60A5FA';   // feature icons, soft accents

// Phoenix orange
const ORANGE      = '#F97316';   // CTA, highlights
const ORANGE_DARK = '#EA580C';   // CTA gradient end, hover

// Light backgrounds (right panel)
const BG_LIGHT    = '#F8FAFC';   // right panel base
const BG_LIGHTER  = '#F1F5F9';   // input bg
const WHITE       = '#FFFFFF';

// Text
const TEXT_HEAD   = '#0F172A';   // primary headings (right panel)
const TEXT_BODY   = '#1E293B';   // body (right panel)
const TEXT_MUTED  = '#64748B';   // secondary text
const TEXT_FAINT  = '#94A3B8';   // placeholders, muted UI

// Text on dark background
const DARK_TEXT_1 = '#E2E8F0';   // primary white-ish text on blue
const DARK_TEXT_2 = '#CBD5E1';   // secondary text on blue
const DARK_TEXT_3 = '#94A3B8';   // muted text on blue

// Borders
const BORDER_LIGHT = '#E2E8F0';  // light panel borders
const BORDER_DARK  = '#334155';  // dark panel borders/dividers

// ─── Unsplash images ───────────────────────────────────────────────────────────
// Layer 1 — dark law library bookshelves (base background, always visible)
const LIBRARY_IMG = 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=85';
// Layer 2 — Lady Justice statue, fades in from right over the library
const STATUE_IMG  = 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=85';
// FACE_1/2/3 (random Unsplash portraits used as fake testimonial
// avatars) were removed alongside the fabricated review.

// ─── CSS ───────────────────────────────────────────────────────────────────────
const STYLES = `
@keyframes float-a  { 0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-16px) rotate(3deg)} }
@keyframes float-b  { 0%,100%{transform:translateY(0)}40%{transform:translateY(-20px)}70%{transform:translateY(-8px)} }
@keyframes float-c  { 0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-14px) scale(1.04)} }
@keyframes pulse-glow { 0%,100%{opacity:0.4;transform:scale(1)}50%{opacity:0.75;transform:scale(1.1)} }
@keyframes particle { 0%{transform:translateY(0) translateX(0);opacity:0}10%{opacity:0.8}90%{opacity:0.3}100%{transform:translateY(-110px) translateX(28px);opacity:0} }
@keyframes particle2{ 0%{transform:translateY(0) translateX(0);opacity:0}10%{opacity:0.6}90%{opacity:0.2}100%{transform:translateY(-85px) translateX(-36px);opacity:0} }
@keyframes slide-up { from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)} }
@keyframes slide-right{from{opacity:0;transform:translateX(-20px)}to{opacity:1;transform:translateX(0)} }
@keyframes fade-in  { from{opacity:0}to{opacity:1} }
@keyframes logo-glow{ 0%,100%{filter:drop-shadow(0 0 18px rgba(249,115,22,0.65)) drop-shadow(0 0 40px rgba(249,115,22,0.25))}50%{filter:drop-shadow(0 0 32px rgba(249,115,22,0.9)) drop-shadow(0 0 60px rgba(249,115,22,0.4))} }
@keyframes wm-breathe { 0%,100%{opacity:0.04;transform:scale(1)}50%{opacity:0.065;transform:scale(1.04)} }
@keyframes preview-float { 0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)} }
@keyframes ticker   { 0%{transform:translateX(0)}100%{transform:translateX(-50%)} }
@keyframes shimmer  { 0%{background-position:-200% center}100%{background-position:200% center} }

.fa  { animation: float-a  9s ease-in-out infinite; }
.fb  { animation: float-b  11s ease-in-out infinite 1s; }
.fc  { animation: float-c  13s ease-in-out infinite 2.5s; }
.pg  { animation: pulse-glow 5s ease-in-out infinite; }
.su  { animation: slide-up   0.55s cubic-bezier(.22,1,.36,1) both; }
.sr  { animation: slide-right 0.55s cubic-bezier(.22,1,.36,1) both; }
.fi  { animation: fade-in  0.4s ease both; }
.lg  { animation: logo-glow 3.5s ease-in-out infinite; }

.ticker-track { animation: ticker 28s linear infinite; white-space:nowrap; }
.ticker-track:hover { animation-play-state: paused; }

.orange-btn {
  background: linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_DARK} 100%);
  box-shadow: 0 4px 18px rgba(249,115,22,0.38);
  transition: all 0.22s ease;
}
.orange-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #FB923C 0%, ${ORANGE} 100%);
  box-shadow: 0 8px 30px rgba(249,115,22,0.52);
  transform: translateY(-1px);
}
.orange-btn:active:not(:disabled) { transform: translateY(0); }

.login-card {
  transition: box-shadow 0.35s ease, transform 0.35s ease;
}
.login-card:hover {
  box-shadow: 0 20px 72px rgba(15,42,95,0.14), 0 6px 20px rgba(249,115,22,0.09) !important;
  transform: translateY(-3px);
}

.right-wm {
  animation: wm-breathe 7s ease-in-out infinite;
  pointer-events: none;
}

.preview-card {
  animation: preview-float 6s ease-in-out infinite;
}

.tab-active   { color:${ORANGE}; border-bottom: 2.5px solid ${ORANGE}; }
.tab-inactive { color:${TEXT_FAINT}; border-bottom: 2.5px solid transparent; }
.tab-inactive:hover { color:${TEXT_MUTED}; }

.input-icon {
  width:100%; padding: 11px 14px 11px 40px;
  border: 1.5px solid ${BORDER_LIGHT}; border-radius: 12px;
  /* 16px on mobile prevents iOS Safari's auto-zoom-on-focus behaviour
     that triggers when font-size is < 16px. Drops to 14px on sm+ to
     match the original visual design. */
  font-size: 16px; background: ${BG_LIGHTER};
  color: ${TEXT_BODY};
  transition: all 0.18s ease; outline: none;
}
@media (min-width: 640px) {
  .input-icon { font-size: 14px; }
}
.input-icon:focus {
  border-color: ${ORANGE}; background: ${WHITE};
  box-shadow: 0 0 0 3px rgba(249,115,22,0.12);
}
.input-icon.error { border-color:#FCA5A5; background:#FFF5F5; }
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────
const parseHash     = () => Object.fromEntries(new URLSearchParams(window.location.hash.replace(/^#\/?/, '')));
const getInviteCode = () => {
  const code = new URLSearchParams(window.location.search).get('invite');
  if (code) { sessionStorage.setItem('inviteCode', code); window.history.replaceState({}, '', window.location.pathname); }
  return sessionStorage.getItem('inviteCode');
};
// Allow letters, spaces, hyphens, apostrophes, and dots (e.g. "Jean-Pierre", "O'Brien")
const NAME_RE  = /^[a-zA-Z\s'\-.]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const vName         = (v, l) => !v.trim() ? `${l} is required` : !NAME_RE.test(v.trim()) ? `${l}: letters, hyphens and apostrophes only` : '';
const vEmail        = (v)    => !v.trim() ? 'Email is required' : !EMAIL_RE.test(v.trim()) ? 'Enter a valid email' : '';
const vConfirmEmail = (v, e) => !v.trim() ? 'Please confirm your email' : v.trim() !== e.trim() ? 'Email addresses do not match' : '';
const vPass         = (v)    => !v ? 'Password is required' : v.length < 8 ? 'Minimum 8 characters' : '';
const vConfirmPass  = (v, p) => !v ? 'Please confirm your password' : v !== p ? 'Passwords do not match' : '';

// ─── Particles ─────────────────────────────────────────────────────────────────
const PARTICLES = Array.from({ length: 22 }, (_, i) => ({
  width:  `${Math.random() * 3.5 + 1.5}px`,
  height: `${Math.random() * 3.5 + 1.5}px`,
  left:   `${Math.random() * 100}%`,
  top:    `${Math.random() * 100}%`,
  background: i % 4 === 0 ? 'rgba(249,115,22,0.9)' : 'rgba(255,255,255,0.6)',
  animation: `${i % 2 === 0 ? 'particle' : 'particle2'} ${7 + Math.random() * 9}s ease-in-out ${Math.random() * 7}s infinite`,
}));

// ─── Ticker content ────────────────────────────────────────────────────────────
//
// Rewritten on 2026-05-19 to remove claims that overstated what the
// product does. The old copy mentioned a "High Court (Accra) cause
// list" auto-update and a "Supreme Court filing deadline" countdown —
// both of which implied live court-data integrations the app doesn't
// actually have. Replacements describe features that are real today:
// matters, deadlines, billing, the client portal.
const TICKER = [
  { icon: CalendarCheck, text: 'Track every deadline. Get reminders before they slip.' },
  { icon: Scale,         text: 'Manage matters, time, billing and client trust in one place.' },
  { icon: Bell,          text: 'Built for Ghana — GH₵ billing, Ghana Card capture, local data residency.' },
  { icon: Scale,         text: 'Your firm. Your cases. Always in control.' },
  { icon: CalendarCheck, text: 'Client portal — share documents and case updates securely.' },
  { icon: Bell,          text: 'Two-way secure messaging with your clients.' },
];

// ─── Firm logos (illustrative placeholders) ───────────────────────────────────
//
// Previously this list contained named "firms" (Owusu & Partners,
// Addo Law Chambers, etc.) presented as if they were DocketFlow
// customers. None of them are — and they read as plausibly real
// Accra firms, which makes the original wording a borderline false-
// testimonial claim. Until we have actual signed referenceable
// customers, this section either stays empty (TrustStrip below
// short-circuits) or uses generic role labels that don't imply
// endorsement.
const FIRMS = [];

// ─── Feature row ───────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const Feature = ({ icon: Icon, text, delay, highlight }) => (
  <div className="flex items-center gap-3 sr" style={{ animationDelay: delay }}>
    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{
        background: highlight ? 'rgba(249,115,22,0.18)' : 'rgba(96,165,250,0.12)',
        border: highlight ? `1px solid rgba(249,115,22,0.45)` : `1px solid rgba(96,165,250,0.25)`,
      }}>
      <Icon size={15} style={{ color: highlight ? ORANGE : BLUE_SOFT }} />
    </div>
    <span className="text-sm" style={{ color: highlight ? '#FED7AA' : DARK_TEXT_1 }}>{text}</span>
    {highlight && (
      <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full"
        style={{ background: 'rgba(249,115,22,0.2)', color: ORANGE }}>New</span>
    )}
  </div>
);

// ─── Stat card ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
const StatCard = ({ icon: Icon, value, label, className, delay }) => (
  <div className={`absolute flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl su ${className}`}
    style={{
      animationDelay: delay,
      background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})`,
      border: '1px solid rgba(255,255,255,0.15)',
    }}>
    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
      <Icon size={18} className="text-white" />
    </div>
    <div>
      <p className="text-white font-extrabold text-xl leading-none">{value}</p>
      <p className="text-orange-100 text-xs mt-0.5 leading-tight">{label}</p>
    </div>
  </div>
);

// ─── Field wrapper ─────────────────────────────────────────────────────────────
const Field = ({ label, error, touched, children }) => (
  <div>
    <label className="block text-sm font-semibold mb-1.5" style={{ color: TEXT_BODY }}>{label}</label>
    {children}
    {touched && error && (
      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
        <AlertCircle size={11} className="flex-shrink-0" />{error}
      </p>
    )}
  </div>
);

// ─── Right-panel phoenix watermark (very faint, orange) ───────────────────────
const RightPanelWatermark = () => (
  <svg viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg"
    className="right-wm absolute"
    style={{ width: '480px', height: '480px', bottom: '-80px', right: '-80px' }}>
    <path d="M100 195 C55 162, 12 128, 24 72 C36 24, 76 4, 100 38 C124 4, 164 24, 176 72 C188 128, 145 162, 100 195Z"
      fill="#F97316" />
    <path d="M100 165 C68 140, 42 115, 52 76 C60 46, 84 30, 100 55 C116 30, 140 46, 148 76 C158 115, 132 140, 100 165Z"
      fill="#FFF" opacity="0.3" />
    <path d="M48 92 C22 70, 2 82, 14 104 C28 82, 54 98, 48 92Z" fill="#F97316" />
    <path d="M152 92 C178 70, 198 82, 186 104 C172 82, 146 98, 152 92Z" fill="#F97316" />
    <circle cx="100" cy="10" r="6" fill="#F97316" />
    <path d="M88 36 C93 22, 100 12, 100 8 C100 12, 107 22, 112 36" stroke="#F97316" strokeWidth="3" fill="none" opacity="0.6" />
  </svg>
);

// ─── Main component ────────────────────────────────────────────────────────────
export const LoginPage = () => {
  const [isLogin,      setIsLogin]      = useState(true);
  const [email,        setEmail]        = useState('');
  const [password,     setPassword]     = useState('');
  const [firstName,    setFirstName]    = useState('');
  const [middleName,   setMiddleName]   = useState('');
  const [lastName,     setLastName]     = useState('');
  const [rememberMe,    setRememberMe]    = useState(false);
  const [isLoading,     setIsLoading]     = useState(false);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [inviteCode,    setInviteCode]    = useState(null);
  const [isInviteFlow,  setIsInviteFlow]  = useState(false);
  const [newPassword,   setNewPassword]   = useState('');
  const [confirmPw,     setConfirmPw]     = useState('');
  const [pwSetting,     setPwSetting]     = useState(false);
  const [touched,       setTouched]       = useState({});
  const [forgotPw,      setForgotPw]      = useState(false);
  const [fpEmail,       setFpEmail]       = useState('');
  const [fpSending,     setFpSending]     = useState(false);
  const [showPw,        setShowPw]        = useState(false);
  const [confirmEmail,  setConfirmEmail]  = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const touch    = (f) => setTouched((t) => ({ ...t, [f]: true }));
  const touchAll = ()  => setTouched({ firstName: true, lastName: true, email: true, confirmEmail: true, password: true, confirmPassword: true });
  const { login, register, resetPassword } = useAuth();
  const msgRef = useRef(null);

  useEffect(() => {
    if ((error || success) && msgRef.current)
      msgRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [error, success]);

  useEffect(() => {
    const code = getInviteCode();
    if (code) { setInviteCode(code); setIsLogin(false); }

    // Route-driven default: when the user arrives via #/signup (e.g. clicked
    // "Start free trial" on the pricing page or "Get started" anywhere else)
    // start them on the Register tab, not Login. The marketing-side `ROUTES`
    // config treats `#/signup` as an alias of login-with-mode=signup; this is
    // where that alias gets honoured.
    const hash = (window.location.hash || '').toLowerCase();
    if (hash.startsWith('#/signup') || hash.startsWith('#signup')) {
      setIsLogin(false);
    }

    // Invite / password-recovery links carry tokens in the URL hash.
    // We must call setSession() with those tokens FIRST so Supabase has an
    // active session before we try to call updateUser(). Clearing the hash
    // before setSession() was the cause of the infinite spinner.
    const params = parseHash();
    if ((params.type === 'invite' || params.type === 'recovery') && params.access_token) {
      supabase.auth.setSession({
        access_token:  params.access_token,
        refresh_token: params.refresh_token || '',
      }).then(({ error }) => {
        // Clear the hash whether or not there was an error
        window.history.replaceState({}, '', window.location.pathname + window.location.search);
        if (!error) setIsInviteFlow(true);
        else setError('This invite link has expired or is invalid. Please request a new one.');
      });
    }
  }, []);

  const fieldErrors = {
    firstName:       vName(firstName, 'First name'),
    lastName:        vName(lastName, 'Last name'),
    middleName:      middleName.trim() && !NAME_RE.test(middleName.trim()) ? 'Letters, hyphens and apostrophes only' : '',
    email:           vEmail(email),
    confirmEmail:    vConfirmEmail(confirmEmail, email),
    password:        vPass(password),
    confirmPassword: vConfirmPass(confirmPassword, password),
  };
  const registerValid = !Object.values(fieldErrors).some(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (!isLogin) { touchAll(); if (!registerValid) return; }
    setIsLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        if (inviteCode) {
          const { error: je } = await joinFirmByInviteCode(inviteCode);
          if (je) throw new Error(je.message);
          sessionStorage.removeItem('inviteCode');
        }
      } else {
        const fullFirst = middleName.trim() ? `${firstName.trim()} ${middleName.trim()}` : firstName.trim();
        await register(email, password, fullFirst, lastName.trim());
        setSuccess('Account created! Check your email to confirm, then log in.');
        setEmail(''); setConfirmEmail(''); setPassword(''); setConfirmPassword('');
        setFirstName(''); setMiddleName(''); setLastName(''); setTouched({});
      }
    } catch (err) { setError(err.message || 'An error occurred. Please try again.'); }
    finally { setIsLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setError('');
    if (!fpEmail.trim() || !EMAIL_RE.test(fpEmail.trim())) { setError('Enter a valid email address.'); return; }
    setFpSending(true);
    try {
      await resetPassword(fpEmail.trim());
      setSuccess('Reset link sent! Check your email inbox.');
      setForgotPw(false);
    } catch (err) { setError(err.message || 'Failed to send reset link.'); }
    finally { setFpSending(false); }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault(); setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPw) { setError('Passwords do not match.'); return; }
    setPwSetting(true);
    const { error: err } = await supabase.auth.updateUser({ password: newPassword });
    if (err) { setPwSetting(false); setError(err.message); return; }
    // Check if this is a portal client or firm staff so we redirect correctly
    const { data: { user } } = await supabase.auth.getUser();
    setPwSetting(false);
    const isPortal = user?.user_metadata?.portal === true;
    setSuccess(isPortal
      ? 'Password set! Taking you to your client portal…'
      : 'Password set! Taking you to the app…');
    setTimeout(() => window.location.replace('/'), 1500);
  };

  const switchTab = (v) => {
    setIsLogin(v); setError(''); setSuccess(''); setTouched({}); setForgotPw(false);
    setConfirmEmail(''); setConfirmPassword('');
  };

  // ── Set-password screen ───────────────────────────────────────────────────────
  if (isInviteFlow) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="flex flex-col min-h-screen">
          <div className="flex flex-1 relative">
            <LeftPanel />
            <CurvedDivider />
            <DashboardPreview />
            <RightPanel>
              <Card>
                <CardLogoHeader />
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})` }}>
                    <KeyRound size={19} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: TEXT_HEAD }}>Set your password</h2>
                    <p className="text-xs" style={{ color: TEXT_MUTED }}>Complete your account setup</p>
                  </div>
                </div>
                <form onSubmit={handleSetPassword} className="space-y-4">
                  <Field label="New password">
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Min. 8 characters" className="input-icon" />
                    </div>
                  </Field>
                  <Field label="Confirm password">
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                        placeholder="Repeat password" className="input-icon" />
                    </div>
                  </Field>
                  {error   && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2 text-sm text-red-700"><AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}</div>}
                  {success && <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex gap-2 text-sm text-green-700"><CheckCircle size={15} className="flex-shrink-0 mt-0.5" />{success}</div>}
                  <button type="submit" disabled={pwSetting || !!success}
                    className="orange-btn w-full text-white font-bold py-3.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 text-sm mt-1">
                    {pwSetting && <Loader size={15} className="animate-spin" />}
                    {success ? 'Redirecting…' : 'Set password & enter dashboard'}
                    {!pwSetting && !success && <ChevronRight size={15} />}
                  </button>
                </form>
              </Card>
            </RightPanel>
          </div>
          <TickerBar />
        </div>
      </>
    );
  }

  // ── Forgot password view ─────────────────────────────────────────────────────
  if (forgotPw) {
    return (
      <>
        <style>{STYLES}</style>
        <div className="flex flex-col min-h-screen">
          <div className="flex flex-1 relative">
            <LeftPanel />
            <CurvedDivider />
            <DashboardPreview />
            <RightPanel>
              <Card>
                <CardLogoHeader />
                <h2 className="text-xl font-bold mb-1" style={{ color: TEXT_HEAD }}>Reset your password</h2>
                <p className="text-sm mb-5" style={{ color: TEXT_MUTED }}>We'll send a reset link to your email.</p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <Field label="Email address">
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)}
                        placeholder="you@example.com" className="input-icon" autoFocus />
                    </div>
                  </Field>
                  {error   && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2 text-sm text-red-700"><AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}</div>}
                  {success && <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex gap-2 text-sm text-green-700"><CheckCircle size={15} className="flex-shrink-0 mt-0.5" />{success}</div>}
                  <button type="submit" disabled={fpSending}
                    className="orange-btn w-full text-white font-bold py-3.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                    {fpSending && <Loader size={15} className="animate-spin" />}
                    Send reset link <ChevronRight size={15} />
                  </button>
                  <button type="button" onClick={() => { setForgotPw(false); setError(''); setSuccess(''); }}
                    className="w-full text-sm transition py-1" style={{ color: TEXT_FAINT }}>
                    ← Back to login
                  </button>
                </form>
              </Card>
            </RightPanel>
          </div>
          <TickerBar />
        </div>
      </>
    );
  }

  // ── Main login / register ─────────────────────────────────────────────────────
  return (
    <>
      <style>{STYLES}</style>
      <div className="flex flex-col min-h-screen">
        <div className="flex flex-1 relative">
          <LeftPanel />
          <CurvedDivider />
          <DashboardPreview />
          <RightPanel>
            <Card>
              <CardLogoHeader />

              {/* Welcome heading */}
              <div className="mb-3 sm:mb-5 su">
                <h2 className="text-xl sm:text-2xl font-bold" style={{ color: TEXT_HEAD }}>
                  {isLogin ? 'Welcome back 👋' : 'Get started free'}
                </h2>
                <p className="text-sm mt-0.5" style={{ color: TEXT_MUTED }}>
                  {isLogin ? 'Securely access your firm dashboard.' : 'Create your DocketFlow account.'}
                </p>
              </div>

              {/* Invite banner */}
              {inviteCode && (
                <div className="mb-5 flex items-center gap-3 p-3.5 rounded-xl su"
                  style={{ background: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.25)' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_DARK})` }}>
                    <UserPlus size={14} className="text-white" />
                  </div>
                  <p className="text-sm font-medium" style={{ color: '#92400e' }}>
                    You've been invited to join a firm. {isLogin ? 'Log in' : 'Register'} to accept.
                  </p>
                </div>
              )}

              {/* Line-style tab switcher */}
              <div className="flex mb-4 sm:mb-6" style={{ borderBottom: `1.5px solid ${BORDER_LIGHT}` }}>
                {[{ label: 'Login', val: true }, { label: 'Register', val: false }].map(({ label, val }) => (
                  <button key={label} onClick={() => switchTab(val)}
                    className={`pb-3 px-5 text-sm font-semibold transition-all ${isLogin === val ? 'tab-active' : 'tab-inactive'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {!isLogin && (
                  <div className="fi space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="First name" error={fieldErrors.firstName} touched={touched.firstName}>
                        <div className="relative">
                          <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                            onBlur={() => touch('firstName')} placeholder="Kofi"
                            className={`input-icon ${touched.firstName && fieldErrors.firstName ? 'error' : ''}`} style={{ paddingLeft: '14px' }} />
                        </div>
                      </Field>
                      <Field label="Last name" error={fieldErrors.lastName} touched={touched.lastName}>
                        <div className="relative">
                          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                            onBlur={() => touch('lastName')} placeholder="Mensah"
                            className={`input-icon ${touched.lastName && fieldErrors.lastName ? 'error' : ''}`} style={{ paddingLeft: '14px' }} />
                        </div>
                      </Field>
                    </div>
                    <Field label="Middle name (optional)" error={fieldErrors.middleName} touched={!!middleName}>
                      <input type="text" value={middleName} onChange={(e) => setMiddleName(e.target.value)}
                        placeholder="Optional"
                        className={`input-icon ${middleName && fieldErrors.middleName ? 'error' : ''}`} style={{ paddingLeft: '14px' }} />
                    </Field>
                  </div>
                )}

                {/* Email */}
                <Field label="Email address" error={fieldErrors.email} touched={!isLogin ? touched.email : false}>
                  <div className="relative">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => touch('email')} placeholder="you@example.com"
                      className={`input-icon ${!isLogin && touched.email && fieldErrors.email ? 'error' : ''}`} />
                  </div>
                </Field>

                {/* Confirm Email — register only */}
                {!isLogin && (
                  <Field label="Confirm email address" error={fieldErrors.confirmEmail} touched={touched.confirmEmail}>
                    <div className="relative">
                      <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
                        onBlur={() => touch('confirmEmail')} placeholder="you@example.com"
                        autoComplete="off"
                        className={`input-icon ${touched.confirmEmail && fieldErrors.confirmEmail ? 'error' : ''}`} />
                    </div>
                  </Field>
                )}

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-sm font-semibold" style={{ color: TEXT_BODY }}>Password</label>
                    {isLogin && (
                      <button type="button" onClick={() => { setForgotPw(true); setError(''); setSuccess(''); }}
                        className="text-xs font-semibold transition" style={{ color: ORANGE }}>
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    <input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => touch('password')} placeholder="••••••••"
                      className={`input-icon pr-10 ${!isLogin && touched.password && fieldErrors.password ? 'error' : ''}`} />
                    <button type="button" onClick={() => setShowPw(v => !v)} tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {touched.password && fieldErrors.password && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <AlertCircle size={11} />{fieldErrors.password}
                    </p>
                  )}
                  {/* Live password rules — shown only during registration */}
                  {!isLogin && password.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {[
                        { label: 'At least 8 characters',        ok: password.length >= 8 },
                        { label: 'At least one uppercase letter', ok: /[A-Z]/.test(password) },
                        { label: 'At least one number',           ok: /[0-9]/.test(password) },
                        { label: 'At least one symbol (!@#$…)',   ok: /[^A-Za-z0-9]/.test(password) },
                      ].map(({ label, ok }) => (
                        <div key={label} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-gray-400'}`}>
                          {ok ? <Check size={11} className="text-green-500" /> : <XIcon size={11} className="text-gray-300" />}
                          {label}
                        </div>
                      ))}
                    </div>
                  )}
                  {!isLogin && !password && <p className="mt-1 text-xs" style={{ color: TEXT_FAINT }}>Minimum 8 characters</p>}
                </div>

                {/* Confirm Password — register only */}
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-semibold mb-1.5" style={{ color: TEXT_BODY }}>Confirm password</label>
                    <div className="relative">
                      <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      <input
                        type={showConfirmPw ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={() => touch('confirmPassword')}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        className={`input-icon pr-10 ${touched.confirmPassword && fieldErrors.confirmPassword ? 'error' : ''}`}
                      />
                      <button type="button" onClick={() => setShowConfirmPw(v => !v)} tabIndex={-1}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        {showConfirmPw ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                    {touched.confirmPassword && fieldErrors.confirmPassword && (
                      <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle size={11} />{fieldErrors.confirmPassword}
                      </p>
                    )}
                    {/* Match indicator */}
                    {confirmPassword.length > 0 && !fieldErrors.confirmPassword && (
                      <p className="mt-1.5 text-xs text-green-600 flex items-center gap-1">
                        <Check size={11} /> Passwords match
                      </p>
                    )}
                  </div>
                )}

                {/* Remember me — login only */}
                {isLogin && (
                  <label className="flex items-center gap-2.5 cursor-pointer select-none">
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 rounded accent-orange-500 cursor-pointer" />
                    <span className="text-sm" style={{ color: TEXT_MUTED }}>Remember me</span>
                  </label>
                )}

                {/* Error / success */}
                {(error || success) && (
                  <div ref={msgRef}>
                    {error   && <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex gap-2 text-sm text-red-700"><AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}</div>}
                    {success && <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex gap-2 text-sm text-green-700"><CheckCircle size={15} className="flex-shrink-0 mt-0.5" />{success}</div>}
                  </div>
                )}

                {/* CTA */}
                <button type="submit" disabled={isLoading}
                  className="orange-btn w-full text-white font-bold py-3.5 rounded-xl disabled:opacity-50 flex items-center justify-center gap-2 text-sm mt-1">
                  {isLoading && <Loader size={15} className="animate-spin" />}
                  {isLogin
                    ? (inviteCode ? 'Login & Join Firm' : 'Enter Your Firm Dashboard')
                    : (inviteCode ? 'Register & Join Firm' : 'Create Your Account')}
                  {!isLoading && <ChevronRight size={15} />}
                </button>
              </form>
            </Card>

            {/* Firm logos trust strip */}
            <TrustStrip />
          </RightPanel>
        </div>

        {/* Bottom ticker */}
        <TickerBar />
      </div>
    </>
  );
};

// ─── Left panel ───────────────────────────────────────────────────────────────
function LeftPanel() {
  return (
    <div className="hidden lg:flex lg:w-[56%] flex-col relative overflow-hidden lg:sticky lg:top-0 lg:h-screen lg:self-start"
      style={{ background: `linear-gradient(145deg, ${BLUE_DEEP} 0%, ${BLUE_MID} 100%)` }}>
      {/* Background composition — two layers */}
      <div className="absolute inset-0">
        <img src={LIBRARY_IMG} alt=""
          className="absolute w-full h-full object-cover"
          style={{ objectPosition: 'center center', opacity: 0.7 }} />
        <img src={STATUE_IMG} alt=""
          className="absolute w-full h-full object-cover"
          style={{
            objectPosition: '70% center', opacity: 0.75,
            maskImage: 'linear-gradient(to right, transparent 20%, rgba(0,0,0,0.6) 45%, black 65%)',
            WebkitMaskImage: 'linear-gradient(to right, transparent 20%, rgba(0,0,0,0.6) 45%, black 65%)',
          }} />
        <div className="absolute inset-0" style={{
          background: `linear-gradient(110deg, ${BLUE_DEEP}DD 0%, ${BLUE_MID}99 30%, ${BLUE_MID}55 55%, transparent 80%)`
        }} />
        <div className="absolute inset-0" style={{
          background: `linear-gradient(to bottom, ${BLUE_DEEP}88 0%, transparent 14%, transparent 80%, ${BLUE_DEEP}AA 100%)`
        }} />
      </div>

      {/* Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {PARTICLES.map((p, i) => <div key={i} className="absolute rounded-full" style={p} />)}
      </div>

      <div className="pg absolute w-96 h-96 rounded-full -top-24 -left-24 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.22) 0%, transparent 65%)' }} />
      <div className="pg absolute w-80 h-80 rounded-full -bottom-16 right-0 pointer-events-none"
        style={{ background: `radial-gradient(circle, rgba(96,165,250,0.15) 0%, transparent 65%)`, animationDelay: '2.5s' }} />


      <div className="absolute pointer-events-none">
        <div className="fa absolute top-[38%] right-[5%] w-12 h-12 rounded-2xl flex items-center justify-center"
          style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)' }}>
          <Scale size={22} style={{ color: ORANGE }} />
        </div>
        <div className="fb absolute top-[60%] right-[8%] w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)' }}>
          <Gavel size={17} style={{ color: 'rgba(255,255,255,0.5)' }} />
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full px-10 py-10 justify-between" style={{ maxWidth: '75%' }}>
        {/* Logo block */}
        <div className="su flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <div className="absolute pointer-events-none"
              style={{
                inset: '-40px -20px -40px -40px',
                background: 'radial-gradient(ellipse 70% 60% at 50% 40%, rgba(249,115,22,0.42) 0%, rgba(249,115,22,0.15) 50%, transparent 75%)',
              }} />
            <img src="/phoenix-bird.png" alt="DocketFlow" className="lg relative"
              style={{ width: '200px', height: '135px', objectFit: 'contain', objectPosition: 'center center', filter: 'drop-shadow(0 0 32px rgba(249,115,22,0.9)) drop-shadow(0 0 12px rgba(249,115,22,0.6))' }} />
          </div>
          <div>
            <div className="font-extrabold leading-none" style={{ fontSize: '1.85rem' }}>
              <span className="text-white">Docket</span><span style={{ color: ORANGE }}>Flow</span>
            </div>
            <div className="text-xs font-bold tracking-widest uppercase mt-1" style={{ color: DARK_TEXT_3 }}>BY PHOENIX TECH</div>
            <div className="flex items-center gap-1.5 mt-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: ORANGE }} />
              <span className="text-xs font-semibold tracking-wider uppercase" style={{ color: DARK_TEXT_3 }}>LEGAL SUITE</span>
            </div>
          </div>
        </div>

        {/* Headline + features */}
        <div className="space-y-7">
          <div className="space-y-4 su" style={{ animationDelay: '0.1s' }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: 'rgba(255,255,255,0.1)', border: `1px solid ${BORDER_DARK}`, color: DARK_TEXT_1 }}>
              <Scale size={11} /> Built for modern African law firms
            </div>
            <h1 className="font-extrabold leading-tight" style={{ fontSize: '2.6rem' }}>
              <span style={{ color: DARK_TEXT_1 }}>Run your entire<br />law firm in<br /></span>
              <span style={{ color: ORANGE }}>one platform.</span>
            </h1>
            <p className="text-sm leading-relaxed" style={{ color: DARK_TEXT_2, maxWidth: '320px' }}>
              From case intake to billing — DocketFlow keeps your firm in control, on every matter, every day.
            </p>
          </div>
          {/* Module grid */}
          <div className="su" style={{ animationDelay: '0.2s' }}>
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: DARK_TEXT_3 }}>Everything your firm needs</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Briefcase,     label: 'Case Management' },
                { icon: BookUser,      label: 'Client & Contacts' },
                { icon: CalendarDays,  label: 'Court Calendar' },
                { icon: Clock,         label: 'Time Tracking' },
                { icon: Receipt,       label: 'Billing & Invoicing' },
                { icon: Wallet,        label: 'Trust Accounting' },
                { icon: UserPlus,      label: 'Client Intake' },
                { icon: Users,         label: 'Staff Management' },
                { icon: ListChecks,    label: 'Task Management' },
                { icon: ShieldCheck,   label: 'Conflict Checking' },
                { icon: AlarmClock,    label: 'Deadline Tracking' },
                { icon: DollarSign,    label: 'Expense Tracking' },
                { icon: FileText,      label: 'Document Management' },
                { icon: BarChart2,     label: 'Reports & Analytics' },
                { icon: Activity,      label: 'Activity Audit Log' },
                { icon: MessageSquare, label: 'Client Messaging' },
                { icon: Smartphone,    label: 'Client Portal',       highlight: true },
                // Was "Court Date Alerts" — implied a court-data integration
                // the app doesn't have. The actual feature is reminders on
                // user-entered deadlines.
                { icon: Bell,          label: 'Deadline Reminders',  highlight: true },
              ].map(({ icon: Icon, label, highlight }, i) => (
                <div key={label}
                  className="flex items-center gap-2.5 px-3 py-2 rounded-xl su"
                  style={{
                    animationDelay: `${0.18 + i * 0.04}s`,
                    background: highlight ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.92)',
                    border: highlight ? '1px solid rgba(249,115,22,0.45)' : '1px solid rgba(255,255,255,0.6)',
                    backdropFilter: 'blur(8px)',
                  }}>
                  <Icon size={13} style={{ color: highlight ? ORANGE : BLUE_UI, flexShrink: 0 }} />
                  <span className="text-xs font-semibold truncate" style={{ color: highlight ? '#FED7AA' : BLUE_DEEP }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ghana focus + product highlights.
            Previously this section featured a fabricated testimonial
            ("Ama Owusu, Managing Partner · Owusu & Partners, Accra")
            plus a 4.9/5 star rating built from three random avatar
            images — invented social proof targeted at a profession
            (lawyers) that is professionally trained to detect exactly
            that kind of deception. Replaced with concrete capability
            claims that ARE true today. When we have a real customer
            who's signed a written authorization, this block becomes
            the home for their actual testimonial. */}
        <div className="su space-y-4" style={{ animationDelay: '0.8s' }}>
          <div className="flex items-center gap-2">
            <span>🇬🇭</span>
            <span className="text-xs font-bold tracking-wide" style={{ color: '#FED7AA' }}>Made for Ghana</span>
            <span className="text-xs" style={{ color: DARK_TEXT_3 }}>·</span>
            <span className="text-xs" style={{ color: DARK_TEXT_3 }}>Practitioner-built</span>
          </div>
          <div style={{ borderTop: `1px solid ${BORDER_DARK}` }} className="pt-4 space-y-3">
            <p className="text-sm leading-relaxed" style={{ color: DARK_TEXT_2 }}>
              Built specifically for Ghanaian practice — GH₵ billing and receipts,
              Ghana Card capture, all sixteen regions in the dropdowns, and a
              role model that mirrors how firms actually staff matters here.
            </p>
            <p className="text-xs" style={{ color: DARK_TEXT_3 }}>
              Currently onboarding our first cohort of firms.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Right panel ──────────────────────────────────────────────────────────────
function RightPanel({ children }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8 py-10 relative"
      style={{ background: BG_LIGHT }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <RightPanelWatermark />
      </div>
      <div className="absolute inset-0 pointer-events-none"
        style={{ backgroundImage: `radial-gradient(circle, ${BLUE_UI}18 1px, transparent 1px)`, backgroundSize: '28px 28px' }} />
      {/* Mobile-only compact logo shown above the card */}
      <div className="lg:hidden text-center mb-3 su">
        <div className="w-12 h-12 rounded-xl mx-auto flex items-center justify-center mb-1.5"
          style={{ background: 'linear-gradient(135deg,#FFF7ED,#FFEDD5)', border: '1px solid rgba(249,115,22,0.25)', boxShadow: '0 4px 20px rgba(249,115,22,0.18)' }}>
          <img src="/logo-transparent.png" alt="DocketFlow" className="w-8 h-8 object-contain" />
        </div>
        <div className="text-base font-extrabold">
          <span className="text-gray-900">Docket</span><span style={{ color: ORANGE }}>Flow</span>
        </div>
      </div>
      <div className="w-full max-w-[430px] relative z-10">
        {children}
        <p className="text-center text-xs mt-4 flex items-center justify-center gap-1.5" style={{ color: TEXT_FAINT }}>
          <ShieldCheck size={12} /> Your data is protected with enterprise-grade security.
        </p>
        <p className="text-center text-xs mt-1 mb-2" style={{ color: TEXT_FAINT }}>
          © {new Date().getFullYear()} Phoenix Tech · All rights reserved.
        </p>
        {/* Escape hatch back to the marketing site. Without this, a user
            who landed on #/login from a bookmark or shared link has no
            way back to the home page short of editing the URL. */}
        <p className="text-center text-xs mb-4">
          <button
            type="button"
            onClick={() => { window.location.hash = '#/'; window.scrollTo(0, 0); }}
            className="font-semibold hover:underline underline-offset-4"
            style={{ color: TEXT_MUTED }}
          >
            ← Back to docketflowapp.com
          </button>
        </p>
      </div>
    </div>
  );
}

// ─── Card shell ───────────────────────────────────────────────────────────────
function Card({ children }) {
  return (
    <div className="login-card bg-white rounded-2xl overflow-hidden"
      style={{ boxShadow: '0 8px 48px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="h-1.5" style={{ background: `linear-gradient(90deg, ${ORANGE}, #1A3A8A)` }} />
      <div className="p-4 sm:p-6 lg:p-8">{children}</div>
    </div>
  );
}

// ─── Card logo header ─────────────────────────────────────────────────────────
// Hidden on small screens — the right panel already shows a compact logo above
// the card on mobile, so this avoids doubling up and wasting vertical space.
function CardLogoHeader() {
  return (
    <div className="hidden sm:flex flex-col items-center text-center mb-5 pb-5" style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
      <div className="relative mb-3">
        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto"
          style={{ background: 'linear-gradient(135deg,#FFF7ED,#FFEDD5)', border: `1px solid rgba(249,115,22,0.22)`, boxShadow: '0 4px 22px rgba(249,115,22,0.2)' }}>
          <img src="/logo-transparent.png" alt="DocketFlow" className="w-11 h-11 sm:w-14 sm:h-14 object-contain" />
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-extrabold leading-none" style={{ color: TEXT_HEAD }}>
        <span>Docket</span><span style={{ color: ORANGE }}>Flow</span>
      </div>
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full"
        style={{ background: 'rgba(249,115,22,0.08)', color: ORANGE, border: `1px solid rgba(249,115,22,0.22)` }}>
        <Scale size={11} /> Legal Suite by Phoenix Tech
      </div>
    </div>
  );
}

// ─── Trust strip ──────────────────────────────────────────────────────────────
//
// Previously claimed "Trusted by 500+ law firms across Ghana" — that
// was false. On launch day we have one onboarded firm. Even later,
// any specific count should reflect reality and update without code
// changes (a Supabase view → API would be ideal).
//
// Replacement copy describes what's actually true: the product is
// built specifically for the Ghanaian legal market, with the design
// decisions that implies (GHS currency, Ghana Card capture, regional
// dropdowns, GLC role conventions). That's a credible, defensible
// claim that doesn't require a customer count to back up.
//
// When the named-firm logo strip returns, it should pull from a
// `public_testimonials` table that only contains firms who've signed
// a written authorization to use their name.
function TrustStrip() {
  return (
    <div className="mt-5 su" style={{ animationDelay: '0.4s' }}>
      <p className="text-center text-xs font-bold" style={{ color: TEXT_BODY }}>
        Built for Ghana law firms 🇬🇭
      </p>
      <p className="text-center mt-1" style={{ fontSize: '10px', color: TEXT_FAINT }}>
        GH₵ billing · Ghana Card capture · regional jurisdictions · GLC role mapping
      </p>
      {FIRMS.length > 0 && (
        <div className="flex items-center justify-between gap-2 mt-3">
          {FIRMS.map((f, i) => (
            <div key={i} className="flex flex-col items-center text-center flex-1">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1"
                style={{ border: `1.5px solid ${BORDER_LIGHT}`, background: WHITE }}>
                <Scale size={13} style={{ color: BLUE_UI }} />
              </div>
              <p className="font-bold leading-tight" style={{ fontSize: '8px', color: TEXT_BODY }}>{f.name}</p>
              {f.sub && <p style={{ fontSize: '7px', color: TEXT_FAINT }}>{f.sub}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Dashboard preview card ───────────────────────────────────────────────────
// Lives in the top-level flex container (NOT inside LeftPanel) so overflow:hidden
// on the left panel doesn't clip it, and z-30 keeps it above CurvedDivider (z-25).
function DashboardPreview() {
  return (
    <div className="preview-card hidden lg:block absolute pointer-events-none"
      style={{
        // Align with right edge of left panel minus a small inset,
        // so the card sits just inside the left panel but fully visible.
        left: 'calc(56% - 230px)',
        bottom: '18%',
        width: '210px',
        zIndex: 30,
      }}>
      <div style={{
        background: 'rgba(10,20,45,0.88)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.14)',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      }}>
        {/* Browser chrome */}
        <div className="flex items-center gap-1.5 px-3 py-2"
          style={{ background: 'rgba(0,0,0,0.35)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="w-2 h-2 rounded-full" style={{ background: '#FF5F57' }} />
          <div className="w-2 h-2 rounded-full" style={{ background: '#FEBC2E' }} />
          <div className="w-2 h-2 rounded-full" style={{ background: '#28C840' }} />
          <div className="flex-1 mx-2 rounded px-2 py-0.5 text-center"
            style={{ background: 'rgba(255,255,255,0.06)', fontSize: '8px', color: 'rgba(255,255,255,0.3)' }}>
            docketflowapp.com/dashboard
          </div>
        </div>

        {/* Mini stat cards */}
        <div className="grid grid-cols-3 gap-1.5 p-2.5">
          {[
            { label: 'Cases',   val: '24',   color: '#3B82F6' },
            { label: 'Tasks',   val: '8',    color: '#F97316' },
            { label: 'Billing', val: '₵12k', color: '#10B981' },
          ].map(s => (
            <div key={s.label} className="rounded-xl p-2 text-center"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="font-bold" style={{ fontSize: '13px', color: s.color }}>{s.val}</div>
              <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.38)', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Mini matters list */}
        <div className="px-2.5 pb-2.5 space-y-1.5">
          <div style={{
            fontSize: '7.5px', color: 'rgba(255,255,255,0.28)', marginBottom: '5px',
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
            Active Matters
          </div>
          {[
            { name: 'Owusu v. ACC',  status: 'Active' },
            { name: 'Mensah Estate', status: 'Review' },
            { name: 'Asante LLC',    status: 'Active' },
          ].map(m => (
            <div key={m.name} className="flex items-center gap-2 rounded-lg px-2 py-1.5"
              style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: m.status === 'Active' ? '#10B981' : '#F97316' }} />
              <span style={{ fontSize: '9px', color: 'rgba(255,255,255,0.6)', flex: 1 }}>{m.name}</span>
              <span className="rounded-full px-1.5 py-0.5" style={{
                fontSize: '7px', fontWeight: 700,
                background: m.status === 'Active' ? 'rgba(16,185,129,0.15)' : 'rgba(249,115,22,0.15)',
                color: m.status === 'Active' ? '#10B981' : '#F97316',
              }}>{m.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Curved panel divider ─────────────────────────────────────────────────────
// Sits absolutely at the left/right panel boundary; the filled path extends the
// left panel's gradient across a gentle outward arc, and a thin orange line marks
// the curve edge. Only visible on lg+ screens where both panels are shown.
function CurvedDivider() {
  return (
    <div className="hidden lg:block absolute pointer-events-none"
      style={{ left: 'calc(56% - 44px)', top: 0, width: '88px', height: '100%', zIndex: 25 }}>
      <svg width="88" height="100%" viewBox="0 0 88 1000"
        preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}>
        <defs>
          {/* Match the left panel's 145deg gradient as closely as possible in SVG */}
          <linearGradient id="curveGrad" x1="0" y1="0" x2="0.3" y2="1">
            <stop offset="0%"   stopColor="#0F2A5F" />
            <stop offset="100%" stopColor="#1E3A8A" />
          </linearGradient>
        </defs>

        {/* Filled region — continues the left panel colour across the curve */}
        <path d="M 0 0 L 44 0 Q 88 500 44 1000 L 0 1000 Z"
          fill="url(#curveGrad)" />

        {/* Subtle inner glow so the curve doesn't look flat */}
        <path d="M 44 0 Q 88 500 44 1000"
          stroke="rgba(96,165,250,0.18)" strokeWidth="18" fill="none" />

        {/* Orange accent line — the visible stylish edge */}
        <path d="M 44 0 Q 88 500 44 1000"
          stroke="#F97316" strokeWidth="2.5" fill="none" opacity="0.85" />
      </svg>
    </div>
  );
}

// ─── Ticker bar ────────────────────────────────────────────────────────────────
function TickerBar() {
  const doubled = [...TICKER, ...TICKER]; // duplicate for seamless loop
  return (
    <div className="flex-shrink-0 overflow-hidden flex items-center gap-4 py-2.5 px-4"
      style={{ background: BLUE_BRAND, borderTop: `1px solid ${BORDER_DARK}`, minHeight: '44px' }}>
      {/* Label */}
      <div className="flex items-center gap-2 flex-shrink-0 pr-3" style={{ borderRight: `1px solid ${BORDER_DARK}` }}>
        <Volume2 size={13} style={{ color: ORANGE }} />
        <span className="text-xs font-bold whitespace-nowrap" style={{ color: DARK_TEXT_1 }}>DocketFlow Updates</span>
      </div>

      {/* Scrolling content */}
      <div className="overflow-hidden flex-1">
        <div className="ticker-track inline-flex items-center gap-8">
          {doubled.map((item, i) => (
            <div key={i} className="inline-flex items-center gap-2 flex-shrink-0">
              <item.icon size={12} style={{ color: ORANGE, flexShrink: 0 }} />
              <span className="text-xs" style={{ color: DARK_TEXT_2 }}>{item.text}</span>
              <span className="text-xs mx-2" style={{ color: BORDER_DARK }}>·</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
